/* =========================================================================
   QUICK PRO — Quick Ship PDP behavior (wave 1 + §12 handlebar revision)
   -------------------------------------------------------------------------
   <qp-qs-main>  — variant selection -> availability-slot state machine,
                   cockpit (bar) picker, gallery sync, ATC/twin-route swap.
                   Reads the JSON blob embedded by sections/qp-qs-main
                   .liquid ([data-qs-data]).
   <qp-qs-bar>   — pinned bottom ATC bar (sections/qp-qs-closing.liquid):
                   shows once the buy-box ATC scrolls above the viewport,
                   mirrors price/availability via the qp-qs:state event.

   State machine (per docs/superpowers/specs/2026-08-04-quick-ship-stock-
   tiers-design.md §4/§6/§12 — "out of stock" never renders):
     us      -> top line "■ QUICK SHIP — IN STOCK, SHIPS FROM USA" + the
                ship promise in the slot.
     factory -> top line "■ QUICK SHIP — IN STOCK, SHIPS FROM OUR FACTORY"
                (wave-2 stub; factory_stock once populated)
     bar     -> PRICED (frame) MODE ONLY: box in stock, chosen bar combo
                built-to-order: route line + inline in-stock alternates
                that re-select the cockpit.
     mto     -> route line to the config twin ("configure your own build"), quiet
                register; whole-color sold out additionally swaps the
                ATC for the twin CTA.
   BAR AVAILABILITY diverges by cockpitPricing (PM live-bug fix
   2026-08-13) — the modes differ in BOTH pricing and line structure,
   keep them explicit:
     included (complete bike) — the bar is EFFECTIVELY INFINITE
       (merchant ruling): every width × stem is selectable, always;
       us_stock is never consulted (the blob does not carry it) and the
       state machine considers ONLY the box variant. A sold-out SIZE
       still routes exactly as before.
     priced (frame) — BAR TRUTH IS us_stock ("us") OR aggregate qty > 0
       (merchant 2026-08-17: handlebar add-on availability must consider
       BOTH the WA and Zipto Chicago warehouses — qty is the Liquid
       aggregate across ALL locations, exactly that union). Real bar
       products are inventoryPolicy CONTINUE, so their "available" is
       always true and never consulted (_barAvail owns this instead).
       The frame's ship mode (us/factory) is decided from the FRAME
       variant alone — merchant ruling 2026-08-17 retired the same-
       warehouse join: the cockpit may ship from a different warehouse
       than the frame, separate packages are expected, and the same-box
       promise is already stripped from the on-page copy. Bar
       purchasability is a wholly separate question, answered only by
       _barAvail.
   previewState (section setting) force_us/force_factory overrides the live
   read so the PM can preview each state before real inventory exists
   (forces BOTH box and bar sides).

   Add-to-cart: when data.bar is present the product-form submit is
   intercepted and replaced with a /cart/add.js items-array add.
     included (bike): ONE line — the box variant — carrying the cockpit
       + as-built spec as properties (_bikeProps: contract agreed with
       fulfilment, consumed by the Airtable QS tab).
     priced (frame): box line (_group_id, _type: parent) + bar line
       (_group_id, _type: addon, Cockpit: "<w> · <l>") when a cockpit
       is chosen; one bare frame line when declined.
   _group_id = <boxVariantId>_qs_<epochms> — format LOAD-BEARING:
   qp-cart-group-map.liquid:183 splits on '_' and matches the FIRST
   segment against the parent line's variant id. Without JS the native
   {% form 'product' %} submits the box alone (fallback). No Globo keys
   are read or written anywhere on this path.
   ========================================================================= */
(function () {
  'use strict';

  var EVT = 'qp-qs:state';

  function esc(s) {
    var d = document.createElement('div');
    d.textContent = s == null ? '' : String(s);
    return d.innerHTML;
  }
  function escAttr(s) { return esc(s).replace(/"/g, '&quot;'); }
  function reducedMotion() {
    return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }
  function onReady(fn) {
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', fn);
    else fn();
  }
  /* Frame (priced) mode money. Format = shop.money_format passed through
     the section JSON (settings.moneyFormat) so the TOTAL matches the
     server-rendered prices around it EXACTLY ("$5,879.00" beside
     "$5,499.00 + $380.00" — never the with-currency register that
     MinimogSettings carries when currency codes are enabled). The bare-$
     fallback only exists so a missing format can never blank the line. */
  function presentmentRate() {
    var c = window.Shopify && window.Shopify.currency;
    var r = c ? parseFloat(c.rate) : NaN;
    return isFinite(r) && r > 0 ? r : 1;
  }
  function fmtMoney(cents, format) {
    var s = (cents / 100).toFixed(2), i = s.indexOf('.');
    var whole = s.slice(0, i).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    var amount = whole + s.slice(i);
    if (format && format.indexOf('amount') !== -1) {
      return format
        .replace(/\{\{\s*amount\s*\}\}/g, amount)
        .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, whole)
        .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, amount.replace(/,/g, ' ').replace('.', ',').replace(/ /g, '.'))
        .replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/g, whole.replace(/,/g, '.'));
    }
    return '$' + amount;
  }

  /* TALK TO US chat trigger fallback. The chat runtime (qpu-chat.js)
     owns [data-qp-open-chat] with a document CAPTURE delegate that
     stopPropagation()s, so this bubble listener can only ever fire when
     that script never loaded — degrade to the merchant's fallback URL
     (trust_3_url) instead of a dead click. No third mechanism: same
     attribute, same delegate contract as the footer/nav/cart. */
  document.addEventListener('click', function (e) {
    if (!e.target || !e.target.closest) return;
    var btn = e.target.closest('.qp-qs__trust-chat[data-qp-open-chat]');
    if (!btn) return;
    var fb = btn.getAttribute('data-qs-chat-fallback');
    if (fb) window.location.href = fb;
  });

  /* ---------------------------------------------------------------- main */
  if (!customElements.get('qp-qs-main')) {
    customElements.define('qp-qs-main', class extends HTMLElement {
      connectedCallback() {
        if (this._initDone) return;
        this._initDone = true;
        onReady(this._setup.bind(this));
      }

      _setup() {
        var dataEl = this.querySelector('[data-qs-data]');
        if (!dataEl) return;
        try { this.data = JSON.parse(dataEl.textContent); } catch (e) { return; }
        this.s = this.data.settings || {};
        this.selection = (this.data.initialOptions || []).slice();
        this.bar = this.data.bar || null;
        this.barSelection = this.bar ? (this.bar.initialOptions || []).slice() : null;
        /* NEVER name this `this.slot`: HTMLElement.prototype.slot is a
           DOMString reflecting the `slot` content attribute, so assigning
           an element stringifies it to "[object HTMLDivElement]" and every
           later read hands back a string — .innerHTML/.classList then
           throw and abort the whole _render pass. */
        this.slotEl = this.querySelector('[data-qs-slot]');
        /* frame (priced) mode — bar price pill + TOTAL line; both render
           server-side and re-price here on every change. The cockpit is
           OPTIONAL here (PM 2026-08-14): barNone starts true — NO
           COCKPIT is the default and the bare frame price leads. In
           included (bike) mode barNone is always false: mandatory. */
        this.priced = !!this.bar && (this.data.settings || {}).cockpitPricing === 'priced';
        /* included (complete bike) mode: mandatory pre-selected cockpit,
           infinite bar side, SINGLE cart line with the properties
           contract (_bikeProps). Distinct from priced in BOTH pricing
           and line structure — never re-merge. */
        this.included = !!this.bar && !this.priced;
        this.barNone = !!this.bar && !!(this.data.settings || {}).cockpitOptional;
        this.commentEl = this.querySelector('[data-qs-comment]');
        this.barPillEl = this.querySelector('[data-qs-bar-pill]');
        this.barNoneBtn = this.querySelector('[data-qs-bar-none]');
        this.cockpitStateEl = this.querySelector('[data-qs-cockpit-state]');
        this.totalEl = this.querySelector('[data-qs-total]');
        this.totalMathEl = this.querySelector('[data-qs-total-math]');
        this._total = null;
        this.toplineEl = this.querySelector('[data-qs-topline]');
        this.toplineTextEl = this.querySelector('[data-qs-topline-text]');
        this.priceEl = this.querySelector('[data-qs-price]');
        this.idInput = this.querySelector('[data-qs-id]');
        this.atc = this.querySelector('[data-qs-atc]');
        this.twinCta = this.querySelector('[data-qs-twin-cta]');
        this.mainImg = this.querySelector('img.qp-qs__media');
        this._slotTimer = null;
        this._adding = false;

        this.addEventListener('click', function (e) {
          var opt = e.target.closest('[data-qs-option]');
          if (opt && this.contains(opt)) { this._pick(opt); return; }
          var barOpt = e.target.closest('[data-qs-bar-option]');
          if (barOpt && this.contains(barOpt)) { this._pickBar(barOpt); return; }
          var barNone = e.target.closest('[data-qs-bar-none]');
          if (barNone && this.contains(barNone) && this.bar) {
            /* explicit NO COCKPIT — back to the bare frame */
            if (!this.barNone) { this.barNone = true; this._render(false); }
            return;
          }
          var alt = e.target.closest('[data-qs-alt]');
          if (alt && this.contains(alt) && this.bar) {
            /* alternate chip re-selects the whole cockpit combo. data-opts
               carries the FULL option set (3-option bars have a colour
               beyond width/stem); data-w/data-l is the legacy fallback. */
            var altOpts = null;
            try { altOpts = JSON.parse(alt.getAttribute('data-opts')); } catch (err) { altOpts = null; }
            if (altOpts && altOpts.length === this.barSelection.length) {
              this.barSelection = altOpts.slice();
            } else {
              this.barSelection[this.bar.widthIndex] = alt.getAttribute('data-w');
              this.barSelection[this.bar.lengthIndex] = alt.getAttribute('data-l');
            }
            this.barNone = false; /* an alternate IS a cockpit choice */
            this._render(false);
            return;
          }
          var salt = e.target.closest('[data-qs-size-alt]');
          if (salt && this.contains(salt) && this.data.sizeIndex >= 0) {
            /* in-stock size alternate re-selects the size */
            this.selection[this.data.sizeIndex] = salt.getAttribute('data-value');
            this._render(false);
            return;
          }
          var thumb = e.target.closest('[data-qs-thumb]');
          if (thumb && this.contains(thumb)) {
            this._showMedia(thumb.getAttribute('data-media-id'), thumb.getAttribute('data-src'));
          }
        }.bind(this));

        /* §12 two-line add — only armed when a bar product is wired; the
           listener also catches the pinned bar's form="" submit. Without
           it (no bar / no JS) the native form posts the box alone. */
        this.form = this.idInput ? this.idInput.form : this.querySelector('form');
        if (this.form && this.bar) {
          this.form.addEventListener('submit', this._submit.bind(this));
        }

        this._render(true);
      }

      _pick(btn) {
        var i = parseInt(btn.getAttribute('data-option-index'), 10);
        var val = btn.getAttribute('data-value');
        if (isNaN(i) || this.selection[i] === val) return;
        this.selection[i] = val;
        this._render(false);
      }

      _pickBar(btn) {
        var i = parseInt(btn.getAttribute('data-option-index'), 10);
        var val = btn.getAttribute('data-value');
        if (isNaN(i) || !this.barSelection) return;
        /* picking any dim chip IS choosing a cockpit — leaves NO COCKPIT
           even when the remembered value is re-clicked unchanged */
        if (this.barSelection[i] === val && !this.barNone) return;
        this.barNone = false;
        this.barSelection[i] = val;
        this._barAutoCorrect(i);
        this._render(false);
      }

      /* Hierarchy auto-correct (PM rule 2026-08-16): picking an OUTER
         value (colour or width) while the remembered inner value has no
         WA stock under it must never leave the picker on a combo the
         customer did not choose and cannot buy — snap the inner levels
         to the FIRST stocked combo under the pick (pick 400 while 70 is
         held -> stem moves to 80). A stem pick (innermost) never
         re-corrects: a dim stem chip is a deliberate routed choice.
         Included (bike) mode / force_* previews: nothing to correct. */
      _barAutoCorrect(i) {
        if (!this.priced || !this._live() || !this.bar) return;
        var li = this.bar.lengthIndex, wi = this.bar.widthIndex;
        if (i === li) return;
        var cur = this._barVariantFor(this.barSelection);
        if (cur && this._barAvail(cur)) return;
        var isWidth = i === wi;
        for (var k = 0; k < this.bar.variants.length; k++) {
          var v = this.bar.variants[k];
          if (!this._barAvail(v)) continue;
          var ok = true;
          for (var j = 0; j < this.barSelection.length; j++) {
            if (j === li) continue;                 /* stem is free to move */
            if (!isWidth && j === wi) continue;     /* a colour pick frees width too */
            if (v.options[j] !== this.barSelection[j]) { ok = false; break; }
          }
          if (ok) { this.barSelection = v.options.slice(); return; }
        }
        /* no stocked combo under this pick — leave it; the routed slot
           (made-to-your-order state) takes over, which is truthful. */
      }

      /* previewState force_* treats every variant as purchasable so the PM
         can walk each state on zero-inventory drafts (preview aid only). */
      _live() { return this.s.previewState !== 'force_us' && this.s.previewState !== 'force_factory'; }
      _avail(v) { return this._live() ? !!v.available : true; }
      /* included (bike) mode: the bar is effectively infinite (merchant
         ruling) — every width × stem selectable, always; us_stock is
         never consulted and the blob does not even carry it.
         priced (frame) mode: BAR truth = us_stock ("us") OR aggregate
         qty > 0 (merchant ruling 2026-08-17: "ensure handlebar add-ons
         will consider both WA and Zipto Chicago warehouse stock" — qty
         is the Liquid `inventory_quantity` aggregate, which already sums
         every location, so qty > 0 covers a combo stocked in Chicago
         only, WA only, or both). Real bar products sell standalone on
         inventoryPolicy CONTINUE, so their `available` is always true
         and means nothing for Quick Ship. force_* still forces it. */
      _barAvail(b) {
        if (this.included) return true;
        return this._live() ? !!(b && (b.us || b.qty > 0)) : true;
      }

      _variantFor(sel) {
        for (var k = 0; k < this.data.variants.length; k++) {
          var v = this.data.variants[k], hit = true;
          for (var i = 0; i < sel.length; i++) {
            if (v.options[i] !== sel[i]) { hit = false; break; }
          }
          if (hit) return v;
        }
        return null;
      }

      _barVariantFor(sel) {
        if (!this.bar || !sel) return null;
        for (var k = 0; k < this.bar.variants.length; k++) {
          var v = this.bar.variants[k], hit = true;
          for (var i = 0; i < sel.length; i++) {
            if (v.options[i] !== sel[i]) { hit = false; break; }
          }
          if (hit) return v;
        }
        return null;
      }

      /* bare numeric option values ("380") get their unit for humans;
         values that already carry one ("38cm") pass through untouched */
      _fmtDim(s) {
        var v = String(s == null ? '' : s);
        return /^\d+(\.\d+)?$/.test(v) ? v + 'mm' : v;
      }

      _barLabel() {
        if (!this.bar || !this.barSelection) return '';
        return this._fmtDim(this.barSelection[this.bar.widthIndex]) + ' · ' + this._fmtDim(this.barSelection[this.bar.lengthIndex]);
      }

      /* "40cm"/"100mm" -> "40"/"100" for the compact alternate chips */
      _digits(s) {
        var d = String(s == null ? '' : s).replace(/[^0-9.]/g, '');
        return d || String(s);
      }

      _state() {
        var v = this._variantFor(this.selection);
        var ci = this.data.colorIndex;
        var colorHasStock = this.data.variants.some(function (vv) {
          return (ci < 0 || vv.options[ci] === this.selection[ci]) && this._avail(vv);
        }.bind(this));
        /* none = the customer's DELIBERATE no-cockpit choice (optional/
           frame mode only): availability is the frame's alone and no
           cockpit routing may apply. bv still resolves the remembered
           combo so the pill can keep quoting the offer price. */
        var none = !!(this.bar && this.barNone);
        var bv = null, barAny = true, barOk = true;
        if (this.bar) {
          bv = this._barVariantFor(this.barSelection);
          barAny = this.bar.variants.some(function (b) { return this._barAvail(b); }.bind(this));
          barOk = !!(bv && this._barAvail(bv));
        }
        var mode;
        if (this.s.previewState === 'force_us') mode = 'us';
        else if (this.s.previewState === 'force_factory') mode = 'factory';
        else if (v && v.available) {
          /* box-only availability: no bar wired, NO COCKPIT chosen,
             included (bike) mode, or (merchant ruling 2026-08-17) a
             purchasable cockpit combo — the bar side never gates the
             FRAME's ship mode. The §12 same-warehouse join (frame and
             bar had to share a warehouse boolean) is retired: the
             cockpit may ship from a different warehouse than the frame,
             separate packages are expected, and that promise is already
             out of the on-page copy. Frame mode reads the frame variant
             alone; bar purchasability is _barAvail's own concern. */
          if (!this.bar || none || this.included || barOk) mode = (!v.us && v.factory) ? 'factory' : 'us';
          else if (barAny) mode = 'bar';
          else mode = 'mto';
        }
        else mode = 'mto';
        return {
          variant: v,
          barVariant: bv,
          mode: mode,
          none: none,
          colorHasStock: colorHasStock,
          barAny: barAny,
          barOk: barOk,
          canBuy: !!(v && this._avail(v)) && (!this.bar || none || barOk)
        };
      }

      /* twin-route swap: whole colorway routed, or no bar combo in stock
         (bar side never routes when the customer chose NO COCKPIT) */
      _routed(st) {
        return !st.colorHasStock || (!!this.bar && !st.none && !st.barAny);
      }

      /* ONE top line above the H1 (P0 FIX A). The lime square means "you
         can buy this today" — routed states return routed:true and the
         square goes visibility:hidden, keeping its space so the H1 holds
         its y-position across every state change. */
      _topline(st) {
        var s = this.s;
        if (st.mode === 'us') return { text: s.toplineUs, routed: false };
        if (st.mode === 'factory') return { text: s.toplineFactory, routed: false };
        if (st.mode === 'bar') return { text: s.toplineCockpit, routed: true };
        var si = this.data.sizeIndex;
        if (this._routed(st) || si < 0) return { text: s.toplineMto, routed: true };
        return {
          text: (s.toplineSize || '').replace('{size}', this.selection[si]),
          routed: true
        };
      }

      _syncTopline(st) {
        if (!this.toplineEl) return;
        var t = this._topline(st);
        if (this.toplineTextEl) this.toplineTextEl.textContent = t.text || '';
        this.toplineEl.classList.toggle('is-routed', t.routed);
      }

      /* WS2 low-stock indicator (merchant 2026-08-17): a plain stated
         fact, never a countdown — only while the item is actually
         purchasable in THIS render (mode us/factory, live inventory, not
         a preview_state force_* walkthrough). {n} is substituted here in
         JS, never inside an inline Liquid output tag (sync trap: no
         brace literals in the settings blob's source). Threshold 0
         (or a missing/blank template string) disables the line
         entirely. Frame line reads the selected box variant; cockpit
         line reads the selected bar combo, ONLY in priced mode and ONLY
         when that combo is the one actually being bought (barOk) — the
         bar side never gets a low-stock line in included (bike) mode,
         under force_* preview, or while it's routed away. */
      _lowStockLines(st) {
        if (!this._live()) return '';
        var threshold = this.s.lowStockThreshold;
        if (!threshold) return '';
        var html = '';
        if (st.variant && typeof st.variant.qty === 'number' && st.variant.qty > 0 && st.variant.qty <= threshold) {
          var ftext = (this.s.lowStockText || '').replace('{n}', st.variant.qty);
          if (ftext) html += '<span class="qp-qs__slot-low">' + esc(ftext) + '</span>';
        }
        if (this.priced && !st.none && st.barOk && st.barVariant
          && typeof st.barVariant.qty === 'number' && st.barVariant.qty > 0 && st.barVariant.qty <= threshold) {
          var btext2 = (this.s.lowStockBarText || '').replace('{n}', st.barVariant.qty);
          if (btext2) html += '<span class="qp-qs__slot-low">' + esc(btext2) + '</span>';
        }
        return html;
      }

      _slotHtml(st) {
        var s = this.s;
        if (st.mode === 'us' || st.mode === 'factory') {
          /* the availability FACT now lives on the top line — the slot
             carries only the promise, in the same register as the routed
             lines (no square, no register change between states). */
          var sub = st.mode === 'us' ? s.usSub : s.factorySub;
          var html = sub ? '<span class="qp-qs__slot-line">' + esc(sub) + '</span>' : '';
          return html + this._lowStockLines(st);
        }
        if (st.mode === 'bar') {
          /* box in stock, chosen cockpit built-to-order: route line + the
             in-stock alternates inline. NEVER the words "out of stock". */
          var btext = (s.barSoldoutLine || '')
            .replace('{bar}', this._barLabel())
            .replace('{window}', s.mtoWindow || '');
          var bhtml = s.twinUrl
            ? '<a class="qp-qs__slot-line qp-qs__slot-line--route" href="' + escAttr(s.twinUrl) + '">' + esc(btext) + '</a>'
            : '<span class="qp-qs__slot-line qp-qs__slot-line--route">' + esc(btext) + '</span>';
          var alts = '', seenAlts = {};
          for (var k = 0; k < this.bar.variants.length; k++) {
            var b = this.bar.variants[k];
            if (!this._barAvail(b)) continue;
            var w = b.options[this.bar.widthIndex], l = b.options[this.bar.lengthIndex];
            var altLbl = this._digits(w) + '·' + this._digits(l);
            /* dedupe on width×stem — a 3-option bar repeats dim pairs
               across colours */
            if (seenAlts[altLbl]) continue;
            seenAlts[altLbl] = true;
            alts += '<button type="button" class="qp-qs__slot-alt" data-qs-alt'
              + ' data-opts="' + escAttr(JSON.stringify(b.options)) + '"'
              + ' data-w="' + escAttr(w) + '" data-l="' + escAttr(l) + '">'
              + esc(altLbl) + '</button>';
          }
          if (alts) {
            bhtml += '<span class="qp-qs__slot-alts" data-qs-alts>'
              + '<span class="qp-qs__slot-alts-label">' + esc(s.barAltLabel || '') + '</span>'
              + alts + '</span>';
          }
          return bhtml;
        }
        /* mto — route to the twin; NEVER the words "out of stock" */
        var si = this.data.sizeIndex, text, sizeRouted = si >= 0 && !this._routed(st);
        if (sizeRouted) {
          text = (s.soldoutSizeLine || '')
            .replace('{size}', this.selection[si])
            .replace('{window}', s.mtoWindow || '');
        } else {
          text = (s.soldoutAllLine || '').replace('{window}', s.mtoWindow || '');
        }
        var html = s.twinUrl
          ? '<a class="qp-qs__slot-line qp-qs__slot-line--route" href="'
            + escAttr(s.twinUrl) + '">' + esc(text) + '</a>'
          : '<span class="qp-qs__slot-line qp-qs__slot-line--route">' + esc(text) + '</span>';
        if (sizeRouted) {
          /* the sizes that ARE boxed right now, inline and re-selectable */
          var alts = '', self = this, seen = {};
          this.data.variants.forEach(function (v) {
            var val = v.options[si];
            if (seen[val]) return;
            var ok = self.data.variants.some(function (vv) {
              if (vv.options[si] !== val || !self._avail(vv)) return false;
              for (var j = 0; j < self.selection.length; j++) {
                if (j !== si && vv.options[j] !== self.selection[j]) return false;
              }
              return true;
            });
            if (!ok) return;
            seen[val] = true;
            alts += '<button type="button" class="qp-qs__slot-alt" data-qs-size-alt'
              + ' data-value="' + escAttr(val) + '">' + esc(val) + '</button>';
          });
          if (alts) {
            html += '<span class="qp-qs__slot-alts" data-qs-alts>'
              + '<span class="qp-qs__slot-alts-label">' + esc(s.barAltLabel || '') + '</span>'
              + alts + '</span>';
          }
        }
        return html;
      }

      _render(first) {
        var st = this._state();
        this._hideAddError(); /* a state change restates the world — a stale
                                 failure notice must not outlive it (the
                                 submit catch re-shows AFTER its render) */
        this._syncOptions();
        this._syncBarOptions();
        this._syncLedger(st);
        this._syncTopline(st);
        this._syncSlot(st, first);
        this._syncAtc(st);
        this._syncPricing(st);
        if (st.variant) {
          if (this.priceEl && st.variant.price) this.priceEl.textContent = st.variant.price;
          if (this.idInput) this.idInput.value = st.variant.id;
          if (!first && st.variant.media_id) {
            this._showMedia(st.variant.media_id, st.variant.media_src);
          }
        }
        this._broadcast(st);
      }

      _syncOptions() {
        var ci = this.data.colorIndex;
        var self = this;
        this.querySelectorAll('[data-qs-option]').forEach(function (btn) {
          var i = parseInt(btn.getAttribute('data-option-index'), 10);
          var val = btn.getAttribute('data-value');
          var selected = self.selection[i] === val;
          btn.classList.toggle('is-selected', selected);
          btn.setAttribute('aria-pressed', selected ? 'true' : 'false');
          var routed;
          if (i === ci) {
            /* colorway-level: any purchasable variant in this color? */
            routed = !self.data.variants.some(function (v) {
              return v.options[i] === val && self._avail(v);
            });
          } else {
            /* combo-level against the currently selected other options */
            routed = !self.data.variants.some(function (v) {
              if (v.options[i] !== val || !self._avail(v)) return false;
              for (var j = 0; j < self.selection.length; j++) {
                if (j !== i && v.options[j] !== self.selection[j]) return false;
              }
              return true;
            });
          }
          btn.classList.toggle('is-routed', routed);
        });
        this.querySelectorAll('[data-qs-value]').forEach(function (el) {
          var i = parseInt(el.getAttribute('data-qs-value'), 10);
          if (!isNaN(i) && self.selection[i] != null) el.textContent = self.selection[i];
        });
      }

      /* cockpit chips — same is-selected/is-routed grammar as size chips,
         combo-level against the other selected bar option */
      _syncBarOptions() {
        if (!this.bar) return;
        var self = this;
        if (this.barNoneBtn) {
          this.barNoneBtn.classList.toggle('is-selected', this.barNone);
          this.barNoneBtn.setAttribute('aria-pressed', this.barNone ? 'true' : 'false');
        }
        this.querySelectorAll('[data-qs-bar-option]').forEach(function (btn) {
          var i = parseInt(btn.getAttribute('data-option-index'), 10);
          var val = btn.getAttribute('data-value');
          /* NO COCKPIT selected: no dim chip may read as chosen */
          var selected = !self.barNone && self.barSelection[i] === val;
          btn.classList.toggle('is-selected', selected);
          btn.setAttribute('aria-pressed', selected ? 'true' : 'false');
          /* HIERARCHY dimming (PM bug fix 2026-08-16): choose-order is
             colour (outer) -> width -> stem (inner). A chip dims only
             when NO stocked variant carries its value together with the
             selections ABOVE it: width chips ignore the stem entirely,
             a colour chip ignores width AND stem, and only the stem row
             reflects the currently selected width — a sellable width
             must never read as routed because of the implied stem. */
          var wi = self.bar.widthIndex, li = self.bar.lengthIndex;
          var routed = !self.bar.variants.some(function (v) {
            if (v.options[i] !== val || !self._barAvail(v)) return false;
            for (var j = 0; j < self.barSelection.length; j++) {
              if (j === i) continue;
              if (i !== li && j === li) continue;         /* width/colour chips: stem is free */
              if (i !== wi && i !== li && j === wi) continue; /* colour chips: width is free too */
              if (v.options[j] !== self.barSelection[j]) return false;
            }
            return true;
          });
          btn.classList.toggle('is-routed', routed);
        });
        this.querySelectorAll('[data-qs-bar-value]').forEach(function (el) {
          var i = parseInt(el.getAttribute('data-qs-bar-value'), 10);
          if (isNaN(i) || self.barSelection[i] == null) return;
          if (self.barNone) { el.textContent = '—'; return; }
          /* the unit lives in the row label ("HANDLEBAR WIDTH (MM)"),
             so legends carry the bare value for every row */
          el.textContent = self.barSelection[i];
        });
      }

      /* dynamic COCKPIT ledger row — "fitted to your build at pack".
         P1 FIX E: the ledger must never state a cockpit that cannot be
         bought, so a routed selection falls back to the merchant's
         stocked default instead of mirroring the routed combo. */
      _syncLedger(st) {
        if (!this.bar) return;
        var dd = this.querySelector('[data-qs-cockpit-dd]');
        if (!dd) return;
        if (st && (st.none || st.barOk === false)) {
          /* NO COCKPIT, or a routed combo: the ledger must never state a
             cockpit that is not part of the purchase */
          dd.textContent = dd.getAttribute('data-qs-cockpit-fallback') || 'Fitted at pack';
          return;
        }
        dd.textContent = (this.s.cockpitRowValue || '{bar}').replace('{bar}', this._barLabel());
      }

      _syncSlot(st, first) {
        var slot = this.slotEl;
        if (!slot || !slot.classList) return;
        var html = this._slotHtml(st);
        if (first || reducedMotion()) {
          slot.innerHTML = html;
          slot.classList.remove('is-swapping');
          return;
        }
        if (this._slotTimer) clearTimeout(this._slotTimer);
        slot.classList.add('is-swapping');
        this._slotTimer = setTimeout(function () {
          slot.innerHTML = html;
          slot.classList.remove('is-swapping');
        }, 120);
      }

      /* frame (priced) mode: EFFECTIVE bar price = min(bundle, list).
         bar.bundleCents (custom.bundle_price on the bar product, gated
         behind the bundle-pricing section setting) is the published
         bundle fact — when it undercuts the variant's list price the
         pill leads with it and strikes the list as a muted compare
         (card discount grammar: no red, no delta badge); null or not
         cheaper collapses to the unchanged real-price pill. bv.cents
         is the LIST price always — never overwrite it: the cart add
         still carries the real variant and the bundle is realised by
         a checkout discount. */
      _barEffCents(bv) {
        if (!bv || bv.cents == null) return null;
        var b = this.bar ? this.bar.bundleCents : null;
        /* bundleCents is SHOP-currency (USD) cents; bv.cents is already
           presentment. The Cart Transform charges bundle x
           presentmentCurrencyRate, so the page applies the same rate
           (Shopify.currency.rate, fallback 1). DISPLAY ONLY — the cart
           add still posts the real variant ids; emission is untouched. */
        if (b != null) b = Math.round(b * presentmentRate());
        return b != null && b < bv.cents ? b : bv.cents;
      }

      /* frame (priced) mode: pill = the bar's effective price (the
         offer — it keeps quoting even under NO COCKPIT); state line +
         TOTAL flip on the same eff/bv this function re-prices on every
         cockpit selection change. Included mode: hard no-op. */
      _syncPricing(st) {
        if (!this.priced) return;
        var bv = st.barVariant;
        var mf = this.s.moneyFormat;
        var eff = this._barEffCents(bv);
        if (this.barPillEl) {
          if (eff == null) {
            this.barPillEl.innerHTML = '';
          } else if (eff < bv.cents) {
            /* bundle undercuts list: same markup as the server render */
            this.barPillEl.innerHTML =
              '<span class="qp-qs__bar-bundle">+' + esc(fmtMoney(eff, mf)) + '</span> ' +
              '<s class="qp-qs__bar-compare"><span class="m:sr-only">' +
              esc(this.s.regularPriceLabel || 'Regular price') + ' </span>' +
              esc(bv.price || fmtMoney(bv.cents, mf)) + '</s>';
          } else {
            this.barPillEl.innerHTML = '<span class="qp-qs__bar-bundle">+'
              + esc(bv.price || fmtMoney(bv.cents, mf)) + '</span>';
          }
        }
        /* P0-1 STATE LINE (design ruling 2026-08-16): full-width flip
           under the COCKPIT label, same re-render path as the pill
           above so the two never disagree. ADDED needs a real price —
           anything short of that (none, or a combo that can't currently
           be priced) reads as the standing NONE offer, never a stale or
           half-built line. Bar name is server data (settings blob);
           width/stem are the live selection, so this always mirrors the
           chips exactly. */
        if (this.cockpitStateEl) {
          if (st.none || !bv || eff == null) {
            this.cockpitStateEl.textContent = 'NO COCKPIT — FRAME SHIPS ALONE';
            this.cockpitStateEl.classList.remove('is-added');
            this.cockpitStateEl.classList.add('is-none');
          } else {
            this.cockpitStateEl.textContent = 'COCKPIT ADDED +' + fmtMoney(eff, mf) + ' — '
              + (this.s.barName || '') + ' ' + this.barSelection[this.bar.widthIndex]
              + ' × ' + this.barSelection[this.bar.lengthIndex];
            this.cockpitStateEl.classList.remove('is-none');
            this.cockpitStateEl.classList.add('is-added');
          }
        }
        /* P0-5 math/TOTAL (design ruling 2026-08-16): ALWAYS rendered in
           priced mode now — content swaps on state change, never
           visibility. `can` is the unchanged purchasability gate
           _total has always used (the pinned bar via _broadcast still
           sees null under NO COCKPIT/unresolved — untouched); only the
           math/value TEXT falls back to the frame-only line instead of
           the whole block hiding. */
        var can = !!(!st.none && st.canBuy && st.variant && bv && st.barOk
          && st.variant.cents != null && eff != null);
        if (can) {
          this._total = fmtMoney(st.variant.cents + eff, mf);
          if (this.totalEl) this.totalEl.textContent = this._total;
          if (this.totalMathEl) {
            this.totalMathEl.textContent = (this.s.totalMathLine || '')
              .replace('{frame}', st.variant.price || fmtMoney(st.variant.cents, mf))
              .replace('{bar}', eff < bv.cents ? fmtMoney(eff, mf) : (bv.price || fmtMoney(bv.cents, mf)));
          }
        } else {
          this._total = null;
          if (this.totalEl) {
            this.totalEl.textContent = st.variant
              ? (st.variant.price || (st.variant.cents != null ? fmtMoney(st.variant.cents, mf) : ''))
              : '';
          }
          if (this.totalMathEl) {
            this.totalMathEl.textContent = this.s.totalMathLineNone || 'FRAME ONLY — NO COCKPIT';
          }
        }
      }

      _syncAtc(st) {
        /* P0 FIX B: ANY state that cannot be purchased swaps the ATC for
           the focusable route <a> (never a disabled primary button — it
           fails contrast and drops out of tab order). The disabled ATC
           survives only as the no-twin fallback. */
        var route = !st.canBuy && !!this.s.twinUrl && !!this.twinCta;
        if (this.atc) {
          this.atc.hidden = route;
          this.atc.disabled = !st.canBuy;
        }
        if (this.twinCta) this.twinCta.hidden = !route;
      }

      /* Size = "<mm>/<letter>" (e.g. "490/M") sourced from the geometry
         section's size_code via its [data-qs-geo-data] blob, key-matched
         to the selected size letter. Falls back to the bare letter when
         no code exists — a bare letter beats a wrong guess. */
      _sizeCode() {
        var si = this.data.sizeIndex;
        if (si < 0) return null;
        var letter = this.selection[si];
        try {
          var el = document.querySelector('[data-qs-geo-data]');
          if (el) {
            var rows = JSON.parse(el.textContent).sizes || [];
            for (var i = 0; i < rows.length; i++) {
              if (rows[i] && rows[i].code && (rows[i].key === letter || rows[i].label === letter)) {
                return rows[i].code;
              }
            }
          }
        } catch (err) { /* malformed geo data: the bare letter stands */ }
        return letter;
      }

      /* INCLUDED (complete bike) single-line properties — contract
         agreed with fulfilment (a dedicated Airtable QS tab consumes
         it). Keys are CLEAN (no Globo trailing-space parity — merchant
         confirmed); values stay MTO-familiar ("380mm", "100mm").
         ORDER MATTERS: _group_id, _type parent (a childless parent is
         supported by qp-cart-group-map and keeps the line in the build
         card), Size <mm>/<letter>, Handlebar Width, Stem Length, Fork
         (fixed "Wide option" — PM: unchangeable on a QS bike), as-built
         spec values (blank = omitted, never invented), optional Comment
         (only when typed), then hidden _bar_variant_id / _bar_sku so
         ops can reconcile bar consumption with no bar line present. */
      _bikeProps(st, gid) {
        var props = { '_group_id': gid, '_type': 'parent' };
        var sc = this._sizeCode();
        if (sc) props['Size'] = sc;
        props['Handlebar Width'] = this._fmtDim(this.barSelection[this.bar.widthIndex]);
        props['Stem Length'] = this._fmtDim(this.barSelection[this.bar.lengthIndex]);
        props['Fork'] = this.s.fork || 'Wide option';
        (this.s.specs || []).forEach(function (pair) {
          if (pair && pair[0] && pair[1] && String(pair[1]).trim() !== '') {
            props[pair[0]] = pair[1];
          }
        });
        if (this.commentEl) {
          var c = String(this.commentEl.value || '').trim();
          if (c) props['Comment'] = c;
        }
        props['_bar_variant_id'] = String(st.barVariant.id);
        props['_bar_sku'] = st.barVariant.sku || '';
        return props;
      }

      /* Add-to-cart — the two modes differ in LINE STRUCTURE, not just
         pricing (never re-merge):
         included (bike): ONE line, the box variant, _bikeProps contract.
         priced (frame): box (_type parent) + bar (_type addon, visible
         Cockpit property) when a cockpit is chosen; NO COCKPIT: ONE
         line — the frame — still stamped _group_id + _type parent: a
         childless parent is supported by qp-cart-group-map and without
         the stamp the line falls out of the build card into OTHER
         ITEMS. */
      _submit(e) {
        if (!this.bar) return; /* wave-1 native path */
        e.preventDefault();
        if (this._adding) return;
        var st = this._state();
        if (!st.canBuy || !st.variant || (!st.none && !st.barVariant)) return;
        var gid = st.variant.id + '_qs_' + Date.now();
        var items;
        if (this.included) {
          items = [{ id: st.variant.id, quantity: 1, properties: this._bikeProps(st, gid) }];
        } else {
          var props = { '_group_id': gid, '_type': 'parent' };
          items = [{ id: st.variant.id, quantity: 1, properties: props }];
          if (!st.none) {
            items.push({
              id: st.barVariant.id,
              quantity: 1,
              properties: { '_group_id': gid, '_type': 'addon', 'Cockpit': this._barLabel() }
            });
          }
        }
        var self = this;
        var barAtc = document.querySelector('[data-qs-bar-atc]');
        var buttons = [this.atc, barAtc];
        this._adding = true;
        buttons.forEach(function (b) { if (b) b.disabled = true; });
        /* 10s timeout: a hung request must never leave the button disabled
           forever with a customer staring at it. AbortController where the
           browser has it; without it the request simply has no timeout
           (old-browser degradation, same as before). */
        var ctrl = typeof AbortController !== 'undefined' ? new AbortController() : null;
        var timer = ctrl ? setTimeout(function () { ctrl.abort(); }, 10000) : null;
        fetch(this.s.addUrl || '/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ items: items }),
          signal: ctrl ? ctrl.signal : undefined
        }).then(function (res) {
          if (timer) clearTimeout(timer);
          if (!res.ok) {
            /* A 422 body carries Shopify's REASON — a validation function's
               own copy (the bundle no-stacking guard) or the stock refusal —
               and that text is written for customers. Read it before throwing
               so the notice can say the truth instead of the generic guess:
               a guard veto shown as "the last one may have just been taken"
               sent us chasing phantom stock for a day. */
            return res.json().catch(function () { return null; }).then(function (body) {
              var httpErr = new Error('qp-qs add failed: HTTP ' + res.status);
              httpErr.status = res.status;
              /* the two cart-add endpoints refuse in different shapes:
                 /cart/add.js -> {status, message, description}
                 /cart/add (routes.cart_add_url, what we post) -> {errors}
                 errors can also be a field->messages object — only a plain
                 string is customer-ready prose; anything else keeps the
                 generic copy. */
              var reason = null;
              if (body) {
                if (typeof body.description === 'string' && body.description) reason = body.description;
                else if (typeof body.errors === 'string' && body.errors) reason = body.errors;
              }
              if (reason) httpErr.description = reason;
              throw httpErr;
            });
          }
          /* 2xx from /cart/add.js means the add went through (the endpoint
             is atomic: with an items array, either every line is added or
             the whole request 422s — it cannot half-succeed). A malformed
             body must therefore never read as failure: swallow the parse
             and land on the cart, where the truth is. */
          return res.json().catch(function () { return null; });
        }).then(function () {
          /* match the wave-1 native post-add behavior: land on the cart */
          window.location.href = self.s.cartUrl || '/cart';
        }).catch(function (err) {
          if (timer) clearTimeout(timer);
          console.error('[qp-qs] cart add failed', err);
          self._adding = false;
          self._render(false); /* restores true button/slot state */
          /* A silent failure reads as "the button does nothing" — the customer
             has no console open. Say something, and say what to do next.
             Register split: a 4xx (almost always 422) is Shopify refusing the
             line — stock gone, or held by someone else's live checkout: a
             real "unavailable" state, so route to another size/colour. A
             timeout or dropped connection is "we could not do this right
             now": retryable, and the add may still have landed, so point at
             the cart. */
          var kind = 'retry';
          if (err && err.name === 'AbortError') kind = 'timeout';
          else if (err && err.status >= 400 && err.status < 500) kind = 'taken';
          self._showAddError(kind, err && err.description);
        });
      }

      _showAddError(kind, detail) {
        var msg;
        if (typeof detail === 'string' && detail) {
          /* Shopify said WHY in words meant for the customer — use them */
          msg = detail;
        } else if (kind === 'timeout') {
          msg = this.s.addErrorTimeoutText ||
            'That took too long, so we stopped waiting. Check your cart in case it went through, or try again in a moment.';
        } else if (kind === 'retry') {
          msg = this.s.addErrorRetryText ||
            'We could not add this just now. Check your connection and try again in a moment.';
        } else {
          msg = this.s.addErrorText ||
            'We could not add that to your cart just now — the last one may have just been taken. Try another size or colour, or refresh and try again.';
        }
        var host = this.querySelector('[data-qs-add-error]');
        if (!host) {
          host = document.createElement('p');
          host.className = 'qp-qs__adderror';
          host.setAttribute('data-qs-add-error', '');
          host.setAttribute('role', 'alert');
          var atc = this.querySelector('[data-qs-atc]');
          if (atc && atc.parentNode) atc.parentNode.insertBefore(host, atc.nextSibling);
          else this.appendChild(host);
        }
        /* When Shopify refused the add AND a discount code is riding on
           the session, the likeliest reason is the bundle no-stacking
           guard — and "remove it to continue" is advice a customer can't
           follow before checkout, because a /discount/CODE link sets an
           INVISIBLE cookie with no UI anywhere. So the notice carries the
           way out: one click drops the code and re-submits the same add.
           If the code wasn't the problem, the retry fails again and says
           the real reason — now without the code muddying it. */
        var offerCodeRemoval = kind === 'taken' && this._hasDiscountCookie();
        var hosts = [host, document.querySelector('[data-qs-bar-error]')];
        var self = this;
        hosts.forEach(function (h) {
          if (!h) return;
          h.textContent = msg;
          if (offerCodeRemoval) {
            var btn = document.createElement('button');
            btn.type = 'button';
            btn.className = 'qp-qs__adderror-action';
            btn.textContent = self.s.addErrorCodeActionText ||
              'Remove the discount code and try again';
            btn.addEventListener('click', function () {
              self._clearDiscountCookie();
              self._hideAddError();
              if (self.form.requestSubmit) self.form.requestSubmit();
              else self._submit({ preventDefault: function () {} });
            });
            h.appendChild(btn);
          }
          h.hidden = false;
        });
      }

      /* The discount_code cookie is what /discount/CODE marketing links
         and checkout-entered codes leave on the session. It is not
         HttpOnly (verified live), so the storefront can both see it and
         drop it — the ONLY pre-checkout surface where a customer can. */
      _hasDiscountCookie() {
        return /(?:^|;\s*)discount_code=[^;]/.test(document.cookie);
      }

      _clearDiscountCookie() {
        var exp = '=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/';
        document.cookie = 'discount_code' + exp;
        /* some sessions carry it on the apex domain */
        document.cookie = 'discount_code' + exp + '; domain=.' + location.hostname.replace(/^www\./, '');
      }

      /* stale alerts lie: any selection change (or a fresh submit) restates
         the world, so the previous failure notice comes down with it. */
      _hideAddError() {
        var host = this.querySelector('[data-qs-add-error]');
        if (host) host.hidden = true;
        var barHost = document.querySelector('[data-qs-bar-error]');
        if (barHost) barHost.hidden = true;
      }

      _showMedia(id, src) {
        if (!src || !this.mainImg) return;
        this.mainImg.src = src;
        this.mainImg.removeAttribute('srcset');
        var idStr = String(id);
        this.querySelectorAll('[data-qs-thumb]').forEach(function (t) {
          t.classList.toggle('is-active', t.getAttribute('data-media-id') === idStr);
        });
      }

      _broadcast(st) {
        var detail = {
          price: st.variant ? st.variant.price : null,
          /* priced (frame) mode: what the customer actually pays with a
             cockpit selected — the pinned bar shows this instead of the
             frame-only price; null under NO COCKPIT (frame price stands) */
          priced: !!this.priced,
          total: this.priced ? this._total : null,
          canBuy: st.canBuy,
          mode: st.mode,
          cockpit: this.bar && !st.none ? this._barLabel() : null,
          route: !st.canBuy && !!this.s.twinUrl,
          twinUrl: this.s.twinUrl || null
        };
        window.__qpQsState = detail;
        document.dispatchEvent(new CustomEvent(EVT, { detail: detail }));
      }
    });
  }

  /* ----------------------------------------------------------------- bar */
  if (!customElements.get('qp-qs-bar')) {
    customElements.define('qp-qs-bar', class extends HTMLElement {
      connectedCallback() {
        if (this._initDone) return;
        this._initDone = true;
        onReady(this._setup.bind(this));
      }

      _setup() {
        this.atc = this.querySelector('[data-qs-bar-atc]');
        this.twin = this.querySelector('[data-qs-bar-twin]');
        this.priceEl = this.querySelector('[data-qs-bar-price]');
        this.stateEl = this.querySelector('[data-qs-bar-state]');
        document.addEventListener(EVT, function (e) { this._apply(e.detail); }.bind(this));
        if (window.__qpQsState) this._apply(window.__qpQsState);
        this._watch();
      }

      _apply(d) {
        if (!d) return;
        /* priced (frame) mode broadcasts a joined TOTAL while a cockpit
           is selected — the pinned bar must quote what the customer
           pays; null under NO COCKPIT, where the frame price IS the pay */
        var p = d.total || d.price;
        if (p && this.priceEl) this.priceEl.textContent = p;
        /* Cockpit state rides with the price (merchant 2026-08-16: the
           bar "needs to consider selected cockpit state" — a joined
           total with no explanation reads as a wrong price). ADDED only
           when the broadcast carries a real joined total, mirroring the
           buy box's own fallback: an unpriceable/routed combo reads
           FRAME ONLY, never a stale claim. Bike (included) pages never
           broadcast priced, so the line stays hidden there. */
        if (this.stateEl) {
          if (d.priced) {
            this.stateEl.textContent = d.total && d.cockpit
              ? '+ COCKPIT ' + d.cockpit
              : 'FRAME ONLY';
            this.stateEl.hidden = false;
          } else {
            this.stateEl.hidden = true;
          }
        }
        var route = d.route && !!this.twin;
        if (this.atc) {
          this.atc.hidden = route;
          this.atc.disabled = !d.canBuy;
        }
        if (this.twin) this.twin.hidden = !route;
      }

      _watch() {
        var main = document.querySelector('qp-qs-main');
        if (!main) return; /* no buy box on this page: bar stays hidden */
        var anchor = main.querySelector('[data-qs-atc-anchor]') || main;
        this.hidden = false;
        var self = this;
        if (!('IntersectionObserver' in window)) {
          self.classList.add('is-visible');
          return;
        }
        var io = new IntersectionObserver(function (entries) {
          entries.forEach(function (en) {
            /* pin only once the buy-box ATC has scrolled ABOVE the viewport */
            var above = !en.isIntersecting && en.boundingClientRect.top < 0;
            self.classList.toggle('is-visible', above);
          });
        }, { threshold: 0 });
        io.observe(anchor);
      }
    });
  }

  /* ------------------------------------------------------------ size fit */
  /* <qp-qs-fit> — FIND MY SIZE modal (snippets/qp-qs-size-finder.liquid).
     Figma entry 588:8, modal canon 72:53. Height (cm AND ft/in — US
     store) -> size recommendation off the [data-qs-fit-data] map string
     ("XS:155:164|…" — source-agnostic: section blocks today, geometry
     metaobject later). Stock truth is COLOURWAY-AWARE (PM live bug
     2026-08-16: the chip-only read judged the recommendation against the
     selected colour and called a size made-to-order while it sat boxed
     in another colour): current-colour truth still comes LIVE off the
     buy-box size chips' is-routed class so the finder always agrees with
     the chip row, and the main element's [data-qs-data] variant blob
     supplies the other colourways. Verdict ladder — stocked in the
     current colour: select the size, as before; stocked only in other
     colour(s): the answer names them and the primary action switches
     colour AND size; stocked nowhere: made-to-order route (config twin)
     PLUS the nearest boxed size (±1 step) with its colourway named —
     never a dead end, never "out of stock". Actions click the real
     chips and ride the existing qp-qs-main pipeline. role=dialog/
     aria-modal, focus trap, ESC, focus back to the trigger. */
  if (!customElements.get('qp-qs-fit')) {
    var FIT_DOMAIN = { min: 148, max: 203 };
    /* axis ticks are unit-native: whole-foot marks in IN mode, round
       centimetres in CM mode (not inch-derived 152/168/183/198) */
    var FIT_TICKS = {
      in: [
        { cm: 152.4, lbl: "5'0\"" },
        { cm: 167.6, lbl: "5'6\"" },
        { cm: 182.9, lbl: "6'0\"" },
        { cm: 198.1, lbl: "6'6\"" }
      ],
      cm: [
        { cm: 150, lbl: '150' },
        { cm: 165, lbl: '165' },
        { cm: 180, lbl: '180' },
        { cm: 195, lbl: '195' }
      ]
    };

    customElements.define('qp-qs-fit', class extends HTMLElement {
      connectedCallback() {
        if (this._initDone) return;
        this._initDone = true;
        onReady(this._setup.bind(this));
      }

      _setup() {
        /* Failed setup used to leave the server-rendered FIND MY SIZE
           trigger in place as a dead button — a click that does nothing is
           the worst failure mode. Any bail-out below hides the whole
           component instead (the size chips above remain the full,
           working path). CSS pairs this with qp-qs-fit:not(:defined)
           so the trigger never renders when the script itself is absent. */
        var dataEl = this.querySelector('[data-qs-fit-data]');
        if (!dataEl) { this.hidden = true; return; }
        try { this.cfg = JSON.parse(dataEl.textContent); } catch (e) { this.hidden = true; return; }
        this.sizes = [];
        var self = this;
        String(this.cfg.map || '').split('|').forEach(function (r) {
          var p = r.split(':');
          var min = parseFloat(p[1]), max = parseFloat(p[2]);
          if (p[0] && isFinite(min) && isFinite(max) && max > min) {
            self.sizes.push({ label: p[0], min: min, max: max });
          }
        });
        if (!this.sizes.length) { this.hidden = true; return; }

        this.main = this.closest('qp-qs-main');
        this.trigger = this.querySelector('[data-qs-fit-open]');
        this.overlay = this.querySelector('[data-qs-fit-overlay]');
        this.modal = this.querySelector('[data-qs-fit-modal]');
        this.input = this.querySelector('[data-qs-fit-input]');
        this.msg = this.querySelector('[data-qs-fit-msg]');
        this.chart = this.querySelector('[data-qs-fit-chart]');
        this.result = this.querySelector('[data-qs-fit-result]');
        if (!this.trigger || !this.overlay || !this.modal) { this.hidden = true; return; }
        this.promptHtml = this.result ? this.result.innerHTML : '';

        this.unit = 'in'; /* US store default */
        /* ONE truth at a time (designer ruling): either the rider's
           typed height (heightCm) OR their explicit row pick (picked) —
           never both. Typing clears picked; a row pick clears the height
           field, heightCm AND the stored qp-fit-cm — a pick must never
           fabricate a measurement the rider did not give. (The old code
           kept both alive and the field refused to update while picked
           was set — how the field said 178 while the panel said XS.) */
        this.heightCm = null;
        this.picked = null;
        try {
          var u = window.localStorage.getItem('qp-fit-unit');
          if (u === 'cm' || u === 'in') this.unit = u;
          var h = parseFloat(window.localStorage.getItem('qp-fit-cm'));
          if (isFinite(h)) this.heightCm = h;
        } catch (e) { /* private mode: no memory, still works */ }

        this._keydown = this._onKeydown.bind(this);
        this.trigger.addEventListener('click', this._open.bind(this));
        this.overlay.addEventListener('click', function (e) {
          if (e.target === self.overlay) self._close();
          var close = e.target.closest('[data-qs-fit-close]');
          if (close) { self._close(); return; }
          var unit = e.target.closest('[data-qs-fit-unit]');
          if (unit) { self._setUnit(unit.getAttribute('data-qs-fit-unit')); return; }
          var row = e.target.closest('[data-qs-fit-row]');
          if (row) { self._pickRow(row.getAttribute('data-size')); return; }
          var use = e.target.closest('[data-qs-fit-use]');
          if (use) { self._use(use.getAttribute('data-size'), use.getAttribute('data-color')); }
        });
        if (this.input) {
          this.input.addEventListener('input', function () {
            var raw = self.input.value;
            var cm = self._parse(raw);
            self.heightCm = cm;
            self.picked = null; /* typed height is now the single truth */
            self._msg(cm == null && String(raw).trim() !== '');
            if (cm != null) {
              try { window.localStorage.setItem('qp-fit-cm', String(cm)); } catch (e) {}
            }
            self._renderAll();
          });
          this.input.addEventListener('keydown', function (e) {
            if (e.key !== 'Enter') return;
            e.preventDefault();
            var primary = self.result && self.result.querySelector('[data-qs-fit-use], a.qp-qs-fit__use');
            if (primary) primary.click();
          });
        }
      }

      /* --- live stock read ---------------------------------------------
         CURRENT-colour truth = the buy-box chips' is-routed class (the
         finder must always agree with the chip row the rider can see).
         OTHER-colour truth = the main element's parsed [data-qs-data]
         blob (options[] + available per variant, colorIndex) — the chips
         are combo-level against the selected colour and carry no
         cross-colour signal (the 2026-08-16 colour-blindness bug). The
         blob read respects the PM's force_* preview via main._avail.
         No blob / no colour axis -> _colorsFor returns null and the
         verdict falls back to chip-only, exactly the old behaviour. */
      _chipAt(index, value) {
        if (!this.main || index == null || index < 0) return null;
        var chips = this.main.querySelectorAll('[data-qs-option][data-option-index="' + index + '"]');
        for (var k = 0; k < chips.length; k++) {
          if (chips[k].getAttribute('data-value') === value) return chips[k];
        }
        return null;
      }
      _chipFor(size) { return this._chipAt(this.cfg.sizeIndex, size); }
      _stocked(size) {
        var chip = this._chipFor(size);
        return !!chip && !chip.classList.contains('is-routed');
      }
      _mainData() {
        var d = this.main && this.main.data;
        return d && d.variants && d.variants.length ? d : null;
      }
      _availV(v) {
        return this.main && this.main._avail ? this.main._avail(v) : !!v.available;
      }
      _currentColor() {
        var d = this._mainData();
        if (!d || d.colorIndex == null || d.colorIndex < 0) return null;
        return this.main.selection ? this.main.selection[d.colorIndex] : null;
      }
      /* ordered, deduped colourway values in which `size` is purchasable;
         null = no colour axis or no blob (caller falls back to chips) */
      _colorsFor(size) {
        var d = this._mainData();
        if (!d || d.colorIndex == null || d.colorIndex < 0 || this.cfg.sizeIndex < 0) return null;
        var ci = d.colorIndex, si = this.cfg.sizeIndex;
        var self = this, out = [], seen = {};
        d.variants.forEach(function (v) {
          var c = v.options[ci];
          if (v.options[si] !== size || seen[c] || !self._availV(v)) return;
          seen[c] = true;
          out.push(c);
        });
        return out;
      }
      /* one verdict per size: current  — boxed in the selected colourway
         (chip truth); others — colourways where it is boxed instead */
      _verdict(size) {
        var colors = this._colorsFor(size);
        var cur = this._currentColor();
        if (colors == null) return { current: this._stocked(size), others: [] };
        return {
          current: this._stocked(size),
          others: colors.filter(function (c) { return c !== cur; })
        };
      }
      /* "Diamond Black" / "A and B" / "A, B and C" / "A, B, C and 2 more" */
      _listColors(arr) {
        var shown = arr.slice(0, 3), extra = arr.length - shown.length;
        if (extra > 0) return shown.join(', ') + ' and ' + extra + ' more';
        if (shown.length === 1) return shown[0];
        return shown.slice(0, -1).join(', ') + ' and ' + shown[shown.length - 1];
      }
      _fill(tpl, map) {
        return String(tpl == null ? '' : tpl).replace(/\{(\w+)\}/g, function (m, k) {
          return map[k] != null ? map[k] : m;
        });
      }

      /* --- height parsing / formatting --------------------------------- */
      _parse(raw) {
        raw = String(raw == null ? '' : raw).trim();
        this._outOfRange = false;
        if (!raw) return null;
        var cm;
        if (this.unit === 'cm') {
          cm = parseFloat(raw.replace(',', '.'));
        } else {
          var m = raw.match(/^(\d+(?:\.\d+)?)\s*(?:'|′|ft|feet)?\s*(\d+(?:\.\d+)?)?\s*(?:\"|″|in|inches)?$/i);
          if (!m) return null;
          var a = parseFloat(m[1]);
          var b = m[2] != null ? parseFloat(m[2]) : null;
          var inches;
          if (b != null) inches = a * 12 + b;      /* 5'10, 5 10, 5ft10 */
          else if (a <= 8) inches = a * 12;        /* bare feet          */
          else if (a >= 120 && a <= 230) return a; /* bare 120–230 in IN
              mode is centimetres — nobody is 120 inches tall (P0: a
              metric rider typing 178 must not get silent nothing) */
          else inches = a;                         /* total inches (70)  */
          cm = inches * 2.54;
        }
        if (isFinite(cm) && (cm < 120 || cm > 230)) {
          /* a real number, just not a rider height we fit — a different
             fact than "could not read that", so say a different thing */
          this._outOfRange = true;
          return null;
        }
        return isFinite(cm) ? cm : null;
      }
      _ftIn(cm) {
        var t = Math.round(cm / 2.54), f = Math.floor(t / 12);
        return f + "'" + (t - f * 12) + '"';
      }
      _fmtHeight(cm) {
        return this.unit === 'cm' ? Math.round(cm) + ' cm' : this._ftIn(cm);
      }
      _rangeLabel(s) {
        return this.unit === 'cm'
          ? s.min + '–' + s.max + ' CM'
          : this._ftIn(s.min) + '–' + this._ftIn(s.max);
      }

      /* explicit pick becomes the single truth: field, heightCm and the
         stored height are CLEARED (designer ruling — never fabricate a
         measurement the rider did not give), so field and panel can
         never contradict each other. */
      _pickRow(label) {
        for (var i = 0; i < this.sizes.length; i++) {
          if (this.sizes[i].label !== label) continue;
          this.picked = label;
          this.heightCm = null;
          if (this.input) this.input.value = '';
          this._msg(false);
          try { window.localStorage.removeItem('qp-fit-cm'); } catch (e) {}
          this._renderAll();
          return;
        }
      }

      /* invalid-entry help line — visible + aria-live, never colour-only
         (the message's presence IS the signal) */
      _msg(show) {
        if (!this.msg) return;
        var t = '';
        if (show) {
          if (this._outOfRange) {
            /* the number read fine — it is simply outside the heights we
               fit. Not the customer's fault; give them a human next step. */
            t = 'That height is outside the range we fit — check the number, or talk to us and we’ll match you by hand.';
          } else {
            t = this.unit === 'cm'
              ? 'Enter a height in cm — like 178.'
              : 'Enter a height like 5\'10" — or switch to CM.';
          }
        }
        this.msg.textContent = t;
      }

      _recommend() {
        var i;
        if (this.picked) {
          for (i = 0; i < this.sizes.length; i++) {
            if (this.sizes[i].label === this.picked) return { size: this.sizes[i], edge: false, picked: true };
          }
        }
        var cm = this.heightCm;
        if (cm == null) return null;
        /* official ranges OVERLAP (e.g. 164-174 / 171-182): collect every
           row whose [min,max] contains the height, then recommend the one
           whose range CENTER sits nearest the input — tie goes to the
           larger size (sizes is ascending by min, so <= keeps the last,
           i.e. largest, match on a tie). The "between sizes" caution line
           already covers the ambiguity for the rider. */
        var matches = [];
        for (i = 0; i < this.sizes.length; i++) {
          if (cm >= this.sizes[i].min && cm <= this.sizes[i].max) matches.push(this.sizes[i]);
        }
        if (matches.length) {
          var bestMatch = matches[0], bestMatchD = Infinity;
          matches.forEach(function (s) {
            var d = Math.abs((s.min + s.max) / 2 - cm);
            if (d <= bestMatchD) { bestMatchD = d; bestMatch = s; }
          });
          return { size: bestMatch, edge: false };
        }
        if (cm < this.sizes[0].min) return { size: this.sizes[0], edge: true };
        var last = this.sizes[this.sizes.length - 1];
        if (cm > last.max) return { size: last, edge: true };
        /* between two ranges (gapped merchant data): nearest midpoint */
        var best = this.sizes[0], bd = Infinity;
        this.sizes.forEach(function (s) {
          var d = Math.abs((s.min + s.max) / 2 - cm);
          if (d < bd) { bd = d; best = s; }
        });
        return { size: best, edge: false };
      }

      /* fit-safety (designer P0): only ever offer a stocked size ONE
         step from the recommendation, sizing DOWN preferred (stem/post
         fit up). Two or more steps off is a mis-fit — the made-to-order
         route stands alone. Colourway-aware (2026-08-16): a step counts
         when boxed in ANY colourway; fit outranks colour, so a size-down
         in another colour beats a size-up in the current one. Returns
         { size, color, switchColor } — color null only when the product
         has no colour axis. */
      _nearestStocked(label) {
        var idx = -1, i, k, cand, v;
        for (i = 0; i < this.sizes.length; i++) {
          if (this.sizes[i].label === label) { idx = i; break; }
        }
        if (idx < 0) return null;
        var steps = [idx - 1, idx + 1];
        for (k = 0; k < steps.length; k++) {
          if (steps[k] < 0 || steps[k] >= this.sizes.length) continue;
          cand = this.sizes[steps[k]];
          v = this._verdict(cand.label);
          if (v.current) return { size: cand, color: this._currentColor(), switchColor: false };
          if (v.others.length) return { size: cand, color: v.others[0], switchColor: true };
        }
        return null;
      }

      /* --- rendering ---------------------------------------------------- */
      _pct(cm) {
        var p = (cm - FIT_DOMAIN.min) / (FIT_DOMAIN.max - FIT_DOMAIN.min) * 100;
        return Math.max(0, Math.min(100, p));
      }

      _renderChart(rec) {
        if (!this.chart) return;
        var self = this;
        var html = '<div class="qp-qs-fit__axis" aria-hidden="true">';
        FIT_TICKS[this.unit === 'cm' ? 'cm' : 'in'].forEach(function (t) {
          html += '<span class="qp-qs-fit__tick" style="left:' + self._pct(t.cm).toFixed(1) + '%">'
            + esc(t.lbl) + '</span>';
        });
        html += '</div>';
        this.sizes.forEach(function (s) {
          var sel = !!(rec && rec.size.label === s.label);
          var stocked = self._stocked(s.label);
          var left = self._pct(s.min), right = self._pct(s.max);
          var cls = 'qp-qs-fit__row' + (sel ? ' is-selected' : '') + (stocked ? '' : ' is-routed');
          var tag = '';
          if (sel) {
            tag = right > 72
              ? '<span class="qp-qs-fit__sel-tag" style="right:calc(' + (100 - left).toFixed(1) + '% + 4px)">Selected</span>'
              : '<span class="qp-qs-fit__sel-tag" style="left:calc(' + right.toFixed(1) + '% + 4px)">Selected</span>';
          }
          html += '<button type="button" class="' + cls + '" data-qs-fit-row'
            + ' data-size="' + escAttr(s.label) + '" aria-pressed="' + (sel ? 'true' : 'false') + '"'
            + ' aria-label="Size ' + escAttr(s.label) + ', rider ' + escAttr(self._rangeLabel(s)) + '">'
            + '<span><span class="qp-qs-fit__size">' + esc(s.label) + '</span>'
            + '<span class="qp-qs-fit__range">' + esc(self._rangeLabel(s)) + '</span></span>'
            + '<span class="qp-qs-fit__track">'
            + '<span class="qp-qs-fit__bar" style="left:' + left.toFixed(1) + '%;width:' + Math.max(2, right - left).toFixed(1) + '%"></span>'
            + tag + '</span></button>';
        });
        this.chart.innerHTML = html;
      }

      _renderResult(rec) {
        if (!this.result) return;
        if (!rec) { this.result.innerHTML = this.promptHtml; return; }
        var s = rec.size;
        var win = this.cfg.mtoWindow || '';
        /* Panel order (PM hierarchy ruling 2026-08-16): (1) the
           recommendation, (2) the availability answer, (3) the
           action(s), (4) the fit caution LAST as a footnote — the
           decision-relevant fact must outrank generic advice. */
        /* label honesty (designer ruling): height-derived = RECOMMENDED,
           explicit pick = YOU PICKED — never "your fit", we did not
           perform a fitting. Both merchant-editable. */
        var label = rec.picked
          ? (this.cfg.labelPicked || 'You picked')
          : (this.cfg.labelRec || 'Recommended');
        var html = '<p class="qp-qs-fit__fitline">' + esc(label) + ' · ' + esc(s.label)
          + ' · Rider ' + esc(this._rangeLabel(s)) + '</p>';
        if (rec.edge) {
          html += '<p class="qp-qs-fit__mtoline">You’re at the edge of our range — '
            + esc(s.label) + ' is the closest fit. Talk to us if you’re unsure.</p>';
        }
        /* colourway-aware availability answer + actions. CTA weight (PM
           ruling): whenever something is boxed — this size in another
           colour, or the nearest size — the boxed route is the PRIMARY
           action and made-to-order drops to the quiet register; made-to-
           order is primary only when nothing is boxed. */
        var v = this._verdict(s.label);
        var actions = '';
        if (v.current) {
          html += '<p class="qp-qs-fit__mtoline">'
            + esc(this._fill(this.cfg.lineStocked, { size: s.label })) + '</p>';
          actions += '<button type="button" class="qp-qs-fit__use" data-qs-fit-use data-size="'
            + escAttr(s.label) + '">Use size ' + esc(s.label) + ' →</button>';
        } else if (v.others.length) {
          /* boxed in other colourway(s): name them (cap 3, then "and N
             more"); each action switches colour AND size in the buy box */
          html += '<p class="qp-qs-fit__mtoline">'
            + esc(this._fill(this.cfg.lineOtherColor, { size: s.label, colors: this._listColors(v.others) }))
            + '</p>';
          var self = this;
          v.others.slice(0, 3).forEach(function (c, n) {
            actions += '<button type="button" class="qp-qs-fit__use'
              + (n === 0 ? '' : ' qp-qs-fit__use--quiet') + '" data-qs-fit-use data-size="'
              + escAttr(s.label) + '" data-color="' + escAttr(c) + '">'
              + esc(self._fill(self.cfg.actionOtherColor, { size: s.label, color: c }))
              + (n === 0 ? ' →' : '') + '</button>';
          });
          if (this.cfg.twinUrl) {
            actions += '<a class="qp-qs-fit__use qp-qs-fit__use--quiet" href="' + escAttr(this.cfg.twinUrl)
              + '">' + esc(this._fill(this.cfg.actionMtoQuiet, { window: win })) + '</a>';
          }
        } else {
          /* boxed in NO colourway — only now made-to-order, and NEVER
             "out of stock"; never a dead end: the nearest boxed size
             (±1 step) rides along with its colourway named. When that
             nearest size exists it is the PRIMARY action (something IS
             boxed) and made-to-order goes quiet; made-to-order leads
             only when nothing is boxed at all. */
          html += '<p class="qp-qs-fit__mtoline">Size ' + esc(s.label)
            + ' is made to your order right now'
            + (win ? ' — about ' + esc(win) : '') + '.</p>';
          var near = this._nearestStocked(s.label);
          if (near) {
            var nearLbl = near.color
              ? this._fill(this.cfg.actionNearestColor, { size: near.size.label, color: near.color })
              : 'Take size ' + near.size.label + ' — boxed now';
            actions += '<button type="button" class="qp-qs-fit__use" data-qs-fit-use data-size="'
              + escAttr(near.size.label) + '"'
              + (near.switchColor ? ' data-color="' + escAttr(near.color) + '"' : '')
              + '>' + esc(nearLbl) + '</button>';
          }
          if (this.cfg.twinUrl) {
            actions += '<a class="qp-qs-fit__use' + (near ? ' qp-qs-fit__use--quiet' : '')
              + '" href="' + escAttr(this.cfg.twinUrl)
              + '">Build it made to order →</a>';
          }
        }
        if (actions) html += '<div class="qp-qs-fit__actions">' + actions + '</div>';
        /* (4) fit caution — stays, but as the closing footnote */
        if (this.cfg.caution) {
          html += '<p class="qp-qs-fit__caution">' + esc(this.cfg.caution) + '</p>';
        }
        this.result.innerHTML = html;
      }

      _renderAll() {
        var rec = this._recommend();
        this._renderChart(rec);
        this._renderResult(rec);
      }

      /* --- unit toggle --------------------------------------------------- */
      _setUnit(u) {
        if (u !== 'cm' && u !== 'in') return;
        this.unit = u;
        try { window.localStorage.setItem('qp-fit-unit', u); } catch (e) {}
        this._applyUnit();
        /* an entry that was invalid under the old unit gets re-read
           under the new one (e.g. 5'10" typed while CM was active) */
        if (this.input && this.heightCm == null && !this.picked && this.input.value.trim() !== '') {
          this.heightCm = this._parse(this.input.value);
          if (this.heightCm != null) {
            try { window.localStorage.setItem('qp-fit-cm', String(this.heightCm)); } catch (e) {}
          }
          this._msg(this.heightCm == null);
        }
        this._renderAll();
      }
      _applyUnit() {
        var self = this;
        this.overlay.querySelectorAll('[data-qs-fit-unit]').forEach(function (b) {
          b.setAttribute('aria-pressed', b.getAttribute('data-qs-fit-unit') === self.unit ? 'true' : 'false');
        });
        if (this.input) {
          /* the placeholder is an EXAMPLE, never a value: the "e.g."
             prefix carries that meaning even where the muted
             ::placeholder colour can't be perceived (PM fix 2026-08-16) */
          this.input.placeholder = this.unit === 'cm' ? 'e.g. 178' : 'e.g. 5\' 10"';
          if (this.heightCm != null) this.input.value = this._fmtHeight(this.heightCm).replace(' cm', '');
        }
      }

      /* --- dialog plumbing ---------------------------------------------- */
      /* P0 stacking, part 2: the chat teaser mounts as an ANONYMOUS fixed
         body child (no id/class — unreachable from CSS) at z 999999 and
         paints over the modal footer at 390. While the dialog is open,
         hide any third-party fixed iframe holder stacked above the
         overlay (z 84); restore exactly on close. The known-id offenders
         (#qpu-launcher etc.) are handled in CSS via body.qp-qs-fit-open. */
      _suppressOverlays() {
        this._suppressed = [];
        var kids = document.body.children;
        for (var k = 0; k < kids.length; k++) {
          var el = kids[k];
          if (el.contains(this) || el.id === 'PBarNextFrameWrapper') continue;
          if (!el.querySelector || !el.querySelector('iframe')) continue;
          var high = false;
          var nodes = [el].concat(Array.prototype.slice.call(el.querySelectorAll('*'), 0, 50));
          for (var j = 0; j < nodes.length; j++) {
            var cs = window.getComputedStyle(nodes[j]);
            var z = parseInt(cs.zIndex, 10);
            if (cs.position === 'fixed' && isFinite(z) && z > 84) { high = true; break; }
          }
          if (!high) continue;
          this._suppressed.push({ el: el, prev: el.style.visibility });
          el.style.setProperty('visibility', 'hidden', 'important');
        }
      }
      _restoreOverlays() {
        (this._suppressed || []).forEach(function (s) {
          if (s.prev) s.el.style.setProperty('visibility', s.prev);
          else s.el.style.removeProperty('visibility');
        });
        this._suppressed = [];
      }

      _open() {
        this.overlay.hidden = false;
        var ov = this.overlay;
        if (reducedMotion()) ov.classList.add('is-open');
        else requestAnimationFrame(function () { ov.classList.add('is-open'); });
        document.body.classList.add('qp-qs-fit-open');
        this._suppressOverlays();
        document.addEventListener('keydown', this._keydown, true);
        this._applyUnit();
        this._renderAll();
        if (this.input) this.input.focus();
      }

      _close() {
        this.overlay.classList.remove('is-open');
        this.overlay.hidden = true;
        document.body.classList.remove('qp-qs-fit-open');
        this._restoreOverlays();
        document.removeEventListener('keydown', this._keydown, true);
        if (this.trigger) this.trigger.focus(); /* spec: focus returns here */
      }

      _use(size, color) {
        /* colour FIRST (data-color = a cross-colourway offer): the size
           chip's routed/stocked read is combo-level against the selected
           colour, so the colourway must land before the size does. Both
           clicks ride the existing qp-qs-main pipeline — no state is
           written here. If a chip has gone (buy-box DOM drift, a
           colourway pulled between render and click), closing silently
           would read as "the button did nothing" — say so instead and
           keep the modal open so the message is actually seen. */
        var applied = true;
        if (color) {
          var d = this._mainData();
          var cchip = d ? this._chipAt(d.colorIndex, color) : null;
          if (cchip) {
            if (!cchip.classList.contains('is-selected')) cchip.click();
          } else {
            applied = false;
          }
        }
        if (size) {
          var chip = this._chipFor(size);
          if (chip) chip.click();
          else applied = false;
        }
        if (!applied) {
          if (this.msg) {
            this.msg.textContent = 'We could not set that for you — close this window and pick size '
              + size + ' in the size row above.';
          }
          return;
        }
        this._close();
      }

      _onKeydown(e) {
        if (this.overlay.hidden) return;
        if (e.key === 'Escape') { e.preventDefault(); this._close(); return; }
        if (e.key !== 'Tab') return;
        /* focus trap — modal is the only reachable surface while open */
        var els = this.modal.querySelectorAll(
          'a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])'
        );
        var list = [];
        for (var k = 0; k < els.length; k++) {
          if (els[k].offsetParent !== null || els[k] === document.activeElement) list.push(els[k]);
        }
        if (!list.length) return;
        var first = list[0], last = list[list.length - 1];
        if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
        else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
        else if (!this.modal.contains(document.activeElement)) { e.preventDefault(); first.focus(); }
      }
    });
  }
})();
